import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowRight, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../services/api';
import './Login.css';

const STATS = [
  { value: 340,  suffix: '+', label: 'AO traités',       delay: 0    },
  { value: 94,   suffix: '%', label: 'Taux de matching', delay: 120  },
  { value: 12,   suffix: 'x', label: 'Gain de temps',    delay: 240  },
];

function useCounter(target, duration = 1600, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

function StatCard({ value, suffix, label, delay, animate }) {
  const count = useCounter(value, 1800, animate);
  return (
    <div className="ln-stat" style={{ animationDelay: `${delay}ms` }}>
      <div className="ln-stat-value">
        {count}<span className="ln-stat-suffix">{suffix}</span>
      </div>
      <div className="ln-stat-label">{label}</div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [animate,  setAnimate]  = useState(false);

  const [forgotOpen,    setForgotOpen]    = useState(false);
  const [forgotEmail,   setForgotEmail]   = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotDone,    setForgotDone]    = useState(false);
  const [forgotError,   setForgotError]   = useState('');

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotDone(true);
    } catch {
      setForgotError('Une erreur est survenue, veuillez réessayer.');
    } finally {
      setForgotLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setAnimate(true), 400);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || String(d)).join(', ')
        : (typeof detail === 'string' ? detail : 'Identifiants incorrects');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ln-shell">
      {/* ── Background ── */}
      <div className="ln-bg-mesh" />
      <div className="ln-orb ln-orb-1" />
      <div className="ln-orb ln-orb-2" />
      <div className="ln-orb ln-orb-3" />
      <div className="ln-grid" />

      {/* ── Left ── */}
      <section className="ln-left">
        <div className="ln-left-inner">

          {/* Brand */}
          <div className="ln-brand">
            <div className="ln-brand-bars">
              <span className="ln-brand-bar" />
              <span className="ln-brand-bar" />
              <span className="ln-brand-bar" />
            </div>
            <div className="ln-brand-text">
              <span className="ln-brand-name">WIBusiness</span>
              <span className="ln-brand-by">by Wikeys</span>
            </div>
          </div>

          {/* Headline */}
          <div className="ln-hero">
            <div className="ln-pill">Plateforme opérationnelle ESN</div>
            <h1 className="ln-headline">
              Pilotez vos AO.<br/>
              <span className="ln-headline-accent">Recrutez plus vite.</span>
            </h1>
            <p className="ln-sub">
              Un outil pensé pour les équipes commerciales et talent — AO, candidats,
              CV IA et CRM dans un seul espace de travail.
            </p>
          </div>

          {/* Stats */}
          <div className="ln-stats">
            {STATS.map((s) => (
              <StatCard key={s.label} {...s} animate={animate} />
            ))}
          </div>

          {/* Floating cards */}
          <div className="ln-cards-wrap">
            <div className="ln-card ln-card-1">
              <div className="ln-card-dot ln-card-dot-green" />
              <div className="ln-card-body">
                <div className="ln-card-title">Matching IA terminé</div>
                <div className="ln-card-desc">Chef de projet digital — SPW Digital</div>
              </div>
              <div className="ln-card-badge ln-card-badge-green">98%</div>
            </div>
            <div className="ln-card ln-card-2">
              <div className="ln-card-dot ln-card-dot-blue" />
              <div className="ln-card-body">
                <div className="ln-card-title">Shortlist validée</div>
                <div className="ln-card-desc">3 profils envoyés au client</div>
              </div>
              <div className="ln-card-badge ln-card-badge-blue">Envoyé</div>
            </div>
            <div className="ln-card ln-card-3">
              <div className="ln-card-dot ln-card-dot-orange" />
              <div className="ln-card-body">
                <div className="ln-card-title">Nouveau AO reçu</div>
                <div className="ln-card-desc">Ingénieur Cloud — Brussels</div>
              </div>
              <div className="ln-card-badge ln-card-badge-orange">Nouveau</div>
            </div>
          </div>

        </div>
      </section>

      {/* ── Right ── */}
      <section className="ln-right">
        <div className="ln-form-wrap">
          <div className="ln-form-glow" />
          <div className="ln-form-card">

            <div className="ln-form-header">
              <div className="ln-form-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <h2 className="ln-form-title">Connexion</h2>
              <p className="ln-form-desc">Accédez à votre espace professionnel</p>
            </div>

            <form onSubmit={handleSubmit} className="ln-form">
              {error && (
                <div className="ln-error">
                  <AlertCircle size={15} />
                  <span>{error}</span>
                </div>
              )}

              <div className="ln-field">
                <label className="ln-label">Adresse e-mail</label>
                <input
                  className="ln-input"
                  type="email"
                  placeholder="prenom.nom@wi-keys.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <div className="ln-field">
                <label className="ln-label">Mot de passe</label>
                <div className="ln-pwd-wrap">
                  <input
                    className="ln-input"
                    type={showPwd ? 'text' : 'password'}
                    placeholder="••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button type="button" className="ln-eye" onClick={() => setShowPwd(v => !v)}>
                    {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              <div className="ln-forgot">
                <button type="button" className="ln-forgot-link" onClick={() => { setForgotOpen(v => !v); setForgotDone(false); setForgotError(''); setForgotEmail(''); }}>
                  Mot de passe oublié ?
                </button>
              </div>

              {forgotOpen && (
                <div className="ln-forgot-box">
                  {forgotDone ? (
                    <div className="ln-forgot-done">
                      <CheckCircle size={16} />
                      <span>Si cet email est associé à un compte, vous recevrez un lien de réinitialisation.</span>
                    </div>
                  ) : (
                    <form onSubmit={handleForgot} className="ln-forgot-form">
                      <p className="ln-forgot-hint">Entrez votre adresse email pour recevoir un lien de réinitialisation.</p>
                      {forgotError && <div className="ln-error" style={{ marginBottom: 10 }}><AlertCircle size={14}/><span>{forgotError}</span></div>}
                      <div className="ln-forgot-row">
                        <input
                          className="ln-input"
                          type="email"
                          placeholder="prenom.nom@wi-keys.com"
                          value={forgotEmail}
                          onChange={e => setForgotEmail(e.target.value)}
                          required
                          autoComplete="email"
                        />
                        <button type="submit" className="ln-forgot-send" disabled={forgotLoading}>
                          {forgotLoading ? <span className="ln-spinner" /> : <ArrowRight size={15} />}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              <button type="submit" className="ln-btn" disabled={loading}>
                {loading ? (
                  <><span className="ln-spinner" /> Connexion…</>
                ) : (
                  <>Se connecter <ArrowRight size={16} /></>
                )}
              </button>
            </form>

            <div className="ln-form-footer">
              <span className="ln-footer-dot" />
              Accès réservé aux collaborateurs Wikeys
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
